<template>
  <v-dialog v-model="dialog" max-width="1600px" persistent scrollable>
    <v-card>
      <v-card-title>
        <span class="text-h6">{{ dialogTitle }}</span>
        <v-spacer />
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text style="max-height: 70vh">
        <v-data-table
          :headers="headers"
          :items="filteredData"
          :loading="loading"
          class="elevation-1"
          :items-per-page="-1"
          hide-default-footer
        >
          <template v-slot:item.insType="{ item }">
            {{ item.insType | translateOptions(insuranceTypeOptions, 'value', 'text') }}
          </template>
          <template v-slot:item.businessDate="{ item }">
            {{ item.businessDate | moment('YYYY-MM-DD') }}
          </template>
          <template v-slot:item.endDate="{ item }">
            <template v-if="item.endDate">{{ item.endDate | moment('YYYY-MM-DD') }}</template>
            <template v-else>—</template>
          </template>
          <template v-slot:item.TEL="{ item }">
            <a
              v-if="item.TEL"
              :href="generateWhatsAppUrl(item)"
              target="_blank"
              class="clickable-link"
              @click.stop
            >
              {{ item.TEL }}
            </a>
            <span v-else>-</span>
          </template>
          <template v-slot:item._RN="{ item }">
            <div class="d-flex align-center flex-wrap">
              <span>{{ item._RN }}</span>
              <v-chip
                v-if="item.RNT === 'RN' && !item.RN"
                label
                color="primary"
                small
              >
                {{ item.RNT }}
              </v-chip>
              <span v-else-if="item.RNT === 'RN' && item.RN">
                {{ item.RN | moment('YYYY-MM-DD') }}
              </span>
              <v-chip v-else-if="item.RNT" label color="default" small>
                {{ item.RNT | translateOptions(rnTypeOptions, 'value', 'text') }}
              </v-chip>
            </div>
          </template>
          <template v-slot:item.agentTel="{ item }">
            <a
              v-if="item.agentTel"
              :href="generateWhatsAppUrl(item.agentTel, item)"
              target="_blank"
              class="clickable-link"
              @click.stop
            >
              {{ item.agentTel }}
            </a>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="openDetails(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="closeDialog">關閉</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { RESTFUL } from '@/data/constants';
import api from '@/api';
import dayjs from 'dayjs';
import { insuranceTypeOptions, rnTypeOptions } from '@/data/optionsConstants';
import { translateOptions } from '@/services/filters';

export default {
  name: 'CRMFilteredTableDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    filterField: {
      type: String,
      required: true,
    },
    filterValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      headers: [
        { text: '客戶名稱', value: 'ContactPerson' },
        { text: '保險種類', value: 'insType' },
        { text: '車牌號碼', value: 'VEHNO' },
        { text: '起保日期', value: 'businessDate' },
        { text: '到期日', value: 'endDate' },
        { text: '保險公司', value: 'NAMEOFINS' },
        { text: '廠商', value: 'MAKE' },
        { text: '型號', value: 'MODEL' },
        { text: '年份', value: 'YEAR' },
        { text: '臨時保單號碼', value: 'CNNO' },
        { text: '正式保單號碼', value: 'POLICYNO' },
        { text: '電話', value: 'TEL' },
        { text: '代理人', value: 'agent' },
        { text: '代理人電話', value: 'agentTel' },
        { text: 'RN', value: '_RN' },
        { text: '檔案編號', value: 'N1' },
        { text: '附件', value: 'depPlace' },
      ],
      filteredData: [],
      loading: false,
      insuranceTypeOptions,
      rnTypeOptions,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    dialogTitle() {
      const fieldNames = {
        ContactPerson: '客戶名稱',
        'data.VEHNO': '車牌號碼',
        'data.CNNO': '臨時保單號碼',
      };
      return `${fieldNames[this.filterField] || '篩選'}: ${this.filterValue}`;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.loadFilteredData();
      }
    },
  },
  methods: {
    async loadFilteredData() {
      if (!this.filterValue) return;

      this.loading = true;
      try {
        let filter = {
          [this.filterField]: this.filterValue,
        };
        if (this.filterField === 'data.CNNO') {
          filter = {
            $expr: {
              $eq: [
                { $arrayElemAt: ['$data.CNNO', -1] }, // Access the last element's "name"
                this.filterValue,
              ],
            },
          };
        }

        const response = await api.get(RESTFUL.crm.list, {
          params: {
            filter: JSON.stringify(filter),
            sort: JSON.stringify({ businessDate: -1 }),
            isSimple: true,
          },
        });

        this.filteredData = response.data.docs || response.data || [];
      } catch (e) {
        console.error('Failed to load filtered data:', e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: e.message || '加載數據失敗',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    closeDialog() {
      this.dialog = false;
      this.filteredData = [];
    },
    openDetails(item) {
      this.$emit('open-details', item._id);
      this.closeDialog();
    },
    generateWhatsAppUrl(item) {
      if (!item.TEL) return '#';

      // Format phone number - remove any spaces, dashes, or special characters
      let phoneNumber = item.TEL.replace(/[\s\-()]/g, '');

      // Prepend 852 if not already present
      if (!phoneNumber.startsWith('852')) {
        phoneNumber = '852' + phoneNumber;
      }

      // Calculate expiry date (businessDate + 1 year)
      const expiryDate = item.businessDate
        ? dayjs(item.businessDate).add(1, 'year').format('YYYY-MM-DD')
        : '';

      // Get customer name and vehicle number
      const customerName = item.ContactPerson || '';
      const vehicleNumber = item.VEHNO || '';
      const insuranceType = translateOptions(item.insType, insuranceTypeOptions, 'value', 'text');

      // Construct the message with dynamic data
      const message = `佑安保險代理丨續保通知：保單將於${expiryDate}到期，被保人${customerName}，保險種類: ${insuranceType}，車牌號碼 (如適用): ${vehicleNumber}。 請聯絡本公司 Bonnie 6696-7236 或 Ava 9373-1297 安排續保。如已辦妥，可不用理會此通知，謝謝！`;

      // URL encode the message
      const encodedMessage = encodeURIComponent(message);

      // Return the full WhatsApp URL
      return `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}
</style>
